import React, { useState } from 'react';
import { SalesAgent } from '../SalesAgent';

// Define types inline to avoid import issues
interface SalesAgentConfig {
  organization_name?: string;
  welcomeMessage?: string;
  product_info?: {
    name?: string;
    description?: string;
    features?: string[];
    benefits?: string[];
  };
  pricing_info?: {
    model?: string;
    starting_price?: string;
    tiers?: string[];
  };
  security_info?: {
    certifications?: string[];
    compliance?: string[];
    data_protection?: string;
  };
  contact_options?: {
    email?: string;
    phone?: string;
    calendar?: string;
    chat?: boolean;
  };
  handoff_criteria?: {
    score_threshold?: number;
    max_messages?: number;
  };
  sandbox_restrictions?: string[];
}

const SalesAgentTest: React.FC = () => {
  const [organizationId] = useState('test-org-123');
  const [config] = useState<SalesAgentConfig>({
    organization_name: 'Test Organization',
    welcomeMessage: 'Hi! I\'m here to help you learn about our amazing solution. What brings you here today?',
    product_info: {
      name: 'Test Solution',
      description: 'An innovative solution for modern businesses',
      features: ['Feature 1', 'Feature 2', 'Feature 3'],
      benefits: ['Benefit 1', 'Benefit 2', 'Benefit 3']
    },
    pricing_info: {
      model: 'Subscription-based',
      starting_price: '$99/month',
      tiers: ['Starter', 'Professional', 'Enterprise']
    },
    security_info: {
      certifications: ['SOC 2', 'ISO 27001'],
      compliance: ['GDPR', 'CCPA'],
      data_protection: 'Enterprise-grade encryption'
    },
    contact_options: {
      email: 'sales@testorg.com',
      phone: '+1-555-123-4567',
      calendar: 'https://calendly.com/testorg'
    },
    handoff_criteria: {
      score_threshold: 75,
      max_messages: 10
    },
    sandbox_restrictions: [
      'Specific pricing for enterprise customers',
      'Internal company policies',
      'Competitor comparisons'
    ]
  });

  const handleHandoff = (handoffData: any) => {
    console.log('🔄 Handoff requested:', handoffData);
    alert(`Handoff requested! Reason: ${handoffData.handoff_reason || 'User requested'}`);
  };

  const handleQualification = (qualificationData: any) => {
    console.log('📊 Lead qualified:', qualificationData);
    // You can integrate with your CRM here
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      padding: '20px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ 
          textAlign: 'center', 
          color: '#333',
          marginBottom: '10px'
        }}>
          🧪 Sales Agent Test Page
        </h1>
        
        <p style={{ 
          textAlign: 'center', 
          color: '#666',
          marginBottom: '40px',
          fontSize: '18px'
        }}>
          Test the AI Sales Agent functionality on this page
        </p>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '40px',
          marginBottom: '40px'
        }}>
          <div>
            <h2 style={{ color: '#333', marginBottom: '20px' }}>📋 Test Information</h2>
            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
              <h3>Organization Details</h3>
              <p><strong>ID:</strong> {organizationId}</p>
              <p><strong>Name:</strong> {config.organization_name}</p>
              
              <h3>Product Information</h3>
              <p><strong>Name:</strong> {config.product_info?.name}</p>
              <p><strong>Description:</strong> {config.product_info?.description}</p>
              
              <h3>Contact Options</h3>
              <p><strong>Email:</strong> {config.contact_options?.email}</p>
              <p><strong>Phone:</strong> {config.contact_options?.phone}</p>
              <p><strong>Calendar:</strong> {config.contact_options?.calendar}</p>
            </div>
          </div>

          <div>
            <h2 style={{ color: '#333', marginBottom: '20px' }}>🎯 Test Scenarios</h2>
            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
              <h3>Try these conversation starters:</h3>
              <ul style={{ lineHeight: '1.6' }}>
                <li>"Tell me about your solution"</li>
                <li>"What are your pricing options?"</li>
                <li>"I need to speak with a sales rep"</li>
                <li>"What security certifications do you have?"</li>
                <li>"Can you show me a demo?"</li>
                <li>"What's your implementation timeline?"</li>
              </ul>
            </div>
          </div>
        </div>

        <div style={{ 
          background: '#e3f2fd', 
          padding: '20px', 
          borderRadius: '8px',
          marginBottom: '40px'
        }}>
          <h3 style={{ color: '#1976d2', marginBottom: '10px' }}>🔍 Testing Notes</h3>
          <ul style={{ color: '#333', lineHeight: '1.6' }}>
            <li>The Sales Agent will appear as a floating chat button (💬) in the bottom-right corner</li>
            <li>Click the button to open the chat interface</li>
            <li>Test different conversation flows and handoff scenarios</li>
            <li>Check the browser console for detailed logs</li>
            <li>Verify that handoff requests trigger alerts</li>
          </ul>
        </div>

        <div style={{ 
          background: '#fff3e0', 
          padding: '20px', 
          borderRadius: '8px'
        }}>
          <h3 style={{ color: '#f57c00', marginBottom: '10px' }}>⚠️ Important Notes</h3>
          <ul style={{ color: '#333', lineHeight: '1.6' }}>
            <li>This is a test environment - no real data will be saved</li>
            <li>The backend API endpoints need to be running for full functionality</li>
            <li>Make sure your backend server is running on the correct port</li>
            <li>Check the network tab for API calls and any errors</li>
          </ul>
        </div>
      </div>

      {/* Sales Agent Component */}
      <SalesAgent
        organizationId={organizationId}
        config={config}
        onHandoff={handleHandoff}
        onQualification={handleQualification}
      />
    </div>
  );
};

export default SalesAgentTest;
